// src/components/ThinkingIndicator.tsx — the reply while it's forming: the same bubble as a message from the EmCog,
// holding what's actually happening — how long it's been, each step taken (what it was and what came of it), and the
// step underway. Nothing rotates through made-up phases; when there are no steps, it says it's thinking and for how long.
import React, { useEffect, useState } from 'react';
import { CapabilityStatus } from '../utils/ConsciousnessMessageHandler';
import '../assets/styles/ThinkingIndicator.css';

interface ThinkingIndicatorProps {
  messageId?: string;
  capabilityStatus?: CapabilityStatus;
  capabilityHistory?: CapabilityStatus[];
  emcogName?: string;
}

/** "12s", "1:05". */
const elapsed = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** A step's words without the trailing dots the server's generic lines carry. */
const words = (text: string | null | undefined): string => String(text || '').replace(/\s*(\.{3}|…)\s*$/, '').trim();

/** What a step was when it's done: its starting words, past tense where it's a plain "-ing" verb. */
const pastOf = (step: CapabilityStatus): string => {
  const started = words(step.started) || words(step.capability?.replace(/_/g, ' '));
  // The leading "-ing" verb: its stem + "ed" (Searching → Searched, Creating → Created, Composing → Composed), or an
  // irregular one.
  const IRREGULAR: Record<string, string> = { Go: 'Went', Writ: 'Wrote', Mak: 'Made', Find: 'Found', Read: 'Read', Draw: 'Drew', Think: 'Thought', Send: 'Sent', Build: 'Built', Runn: 'Ran', Gett: 'Got', Sett: 'Set', Putt: 'Put', Weav: 'Wove', Tak: 'Took', Giv: 'Gave', Bring: 'Brought', Buy: 'Bought', Sing: 'Sang', Pay: 'Paid' };
  return started.replace(/^([A-Z][a-z]+)ing\b/, (_whole, stem: string) => IRREGULAR[stem] || (/[^aeiou]y$/.test(stem) ? `${stem.slice(0, -1)}ied` : `${stem}ed`));
};

/** What came of it — the server's few words ("6 results", the title), unless it's only the generic "done". */
const outcomeOf = (step: CapabilityStatus): string => {
  const said = words(step.message);
  if (!said || /^(.+ )?(complete|completed|created|done|checked|composed|updated|connected|retrieved|read|sent|posted|followed|unfollowed|liked|unliked|retweeted|unretweeted|traced|processed|rendering|active)$/i.test(said)) return '';
  return said;
};

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ messageId, capabilityStatus, capabilityHistory = [], emcogName = 'Max' }) => {
  const [since] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const active = capabilityStatus?.status === 'starting' && capabilityStatus.message ? capabilityStatus : null;
  const done = capabilityHistory.filter(s => (s.status === 'complete' || s.status === 'error') && (s.timestamp || 0) >= since - 1000).slice(-4);
  const working = !!active || done.length > 0;
  const time = now - since;

  return (
    <div className="message-bubble sender-emcog thinking-bubble" data-message-id={messageId} role="status" aria-live="polite">
      <div className="sender-name">{emcogName}</div>
      <div className="message-content ti">
        <div className="ti-head">
          <span className="ti-orb" aria-hidden="true" />
          <span>{working ? 'Working on it' : 'Thinking'}</span>
          {time >= 3000 && <span className="ti-time">{elapsed(time)}</span>}
        </div>
        {(done.length > 0 || active) && (
          <ol className="ti-steps">
            {done.map(step => {
              const outcome = outcomeOf(step);
              const failed = step.status === 'error';
              return (
                <li key={`${step.capability}-${step.timestamp}`} className={`ti-step ${failed ? 'ti-failed' : 'ti-done'}`}>
                  <span className="ti-mark" aria-label={failed ? 'Did not work' : 'Done'}>{failed ? '✕' : '✓'}</span>
                  <span className="ti-what">{pastOf(step)}{outcome && !failed && <span className="ti-out"> · {outcome}</span>}</span>
                  {outcome && failed && <span className="ti-why">{outcome}</span>}
                </li>
              );
            })}
            {active && (
              <li key={`now-${active.timestamp}`} className="ti-step ti-now">
                <span className="ti-spin" aria-hidden="true" />
                <span className="ti-what">{words(active.message)}</span>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  );
};

export default ThinkingIndicator;
